import {
  QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
} from "@tanstack/react-query";
import { pushEcommerceEvent } from "@wbly/gtm";
import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";

import {
  AddToCartArgs,
  AddToCartChildLineItem,
  AddToCartParentLineItem,
  AddToCartResponse,
  GetCartArgs,
  GetCartResponse,
  UpdateCartItemArgs,
  UpdateCartItemResponse,
  UseAddToCartResponse,
  UseGetCartResponse,
  UseUpdateCartItemResponse,
} from "types/queries";
import {
  getMissingKeysFromObject,
  missingCookiesError,
} from "utils/error-utils";

import { useGetCsrfToken } from "./website-queries";

/**
 * Cart query keys.
 *
 * These uniquely identify queries in the `react-query` cache.
 *
 * [More on this pattern](https://tkdodo.eu/blog/effective-react-query-keys)
 */
export const cartKeys = {
  cart: [{ scope: "cart", data: "cart" }] as const,
};

/**
 * Set defaults for cart queries
 *
 * Setting these here applies them globally whether called directly or via custom hooks
 *
 * Also helpful for testing as they can be easily overriden
 * (e.g. to stop retries from slowing tests down)
 */
export function setCartQueryDefaults(queryClient: QueryClient) {
  queryClient.setQueryDefaults([{ data: "cart" }], {
    // Consider cart data fresh for one minute
    staleTime: 60_000,
    // This stops the cart returning `undefined` between changes
    keepPreviousData: true,
  });
}

/************************************************************************************************
 * QUERIES
 *
 * For each query, we define a hook and a fetcher function.
 *
 * Each hook wraps `useQuery` with configuration on when to fetch and parameters.
 *
 * The fetcher is separate from the hook for use outside of React components (e.g. on server).
 ************************************************************************************************/

/**
 * Get the user's cart
 * proxies to eagle via nginx
 */
async function getCart({
  countryCode,
  csrfToken,
  currency,
  guestToken,
  locale,
  restrictedUserId,
}: GetCartArgs) {
  const response = await axios.get<GetCartResponse>("/website/v2/cart", {
    headers: {
      Lmn_country_iso: countryCode,
      Lmn_currency: currency,
      Lmn_guest_token: guestToken,
      Lmn_restricted_user: restrictedUserId,
      Lmn_locale: locale,
      "X-Csrf-Token": csrfToken,
    },
  });

  return response.data.data;
}

/**
 * Query to get the user's cart.
 * Proxies to eagle via nginx
 */
export function useGetCart() {
  const { data: csrfToken } = useGetCsrfToken();

  return useQuery<UseGetCartResponse | null, AxiosError>({
    queryKey: cartKeys.cart,
    queryFn: async () => {
      const {
        country_code: countryCode,
        currency,
        guest_token,
        guestToken,
        locale,
        restricted_user_id,
        restrictedUserId,
      } = Cookies.get();

      const missingCookies = getMissingKeysFromObject({
        countryCode,
        currency,
        locale,
      });

      if (missingCookies) {
        throw missingCookiesError(missingCookies);
      }

      return getCart({
        countryCode,
        csrfToken: csrfToken!,
        currency,
        guestToken: guestToken ?? guest_token,
        locale,
        restrictedUserId: restrictedUserId ?? restricted_user_id,
      });
    },
    enabled: !!csrfToken,
  });
}

/**
 * Add the provided product to the user's cart. If `addOnSkus` are provided,
 * only they will be added as Canvas adds the parent line item to cart at the Formats step.
 * Proxies to eagle via nginx
 */
async function addToCart({
  addOnSkus,
  csrfToken,
  customisation,
  productSku,
}: AddToCartArgs): Promise<UseAddToCartResponse> {
  const {
    country_code,
    currency,
    guest_token,
    guestToken,
    locale,
    restricted_user_id,
    restrictedUserId,
  } = Cookies.get();

  const missingCookies = getMissingKeysFromObject({
    country_code,
    currency,
    locale,
  });

  if (missingCookies) {
    throw missingCookiesError(missingCookies);
  }

  let items: (AddToCartParentLineItem | AddToCartChildLineItem)[] = [];

  if (addOnSkus) {
    // Add-ons have a different structure to the parent line item
    addOnSkus.forEach((sku) => items.push({ sku, childLineItem: true }));
  } else {
    items.push({
      productId: productSku,
      customisations: customisation,
    });
  }

  const { data } = await axios.post<AddToCartResponse>(
    "/website/v2/cart/items",
    {
      items,
    },
    {
      headers: {
        Lmn_locale: locale,
        Lmn_currency: currency,
        Lmn_country_iso: country_code,
        Lmn_guest_token: guestToken ?? guest_token,
        Lmn_restricted_user: restrictedUserId ?? restricted_user_id,
        "X-Csrf-Token": csrfToken,
      },
    }
  );

  // get the last item which was added to the cart
  const addedItem = data.data.order.items[data.data.order.items.length - 1];

  // best to operate on caution, don't want to block cart
  if (addedItem) {
    pushEcommerceEvent({
      name: "add_to_cart",
      urgency: "low",
      item: {
        item_id: addedItem.productSku,
        item_name: addedItem.name,
        price: Number(addedItem.price.amount),
        quantity: 1,
      },
    });
  }

  return {
    customisation,
    addOnSkus,
    newGuestToken: data.data.newGuestToken,
    /**
     * Because Canvas adds to cart at the Formats step, we need the cart
     * item id to update it with Giftwraps and Add-ons.
     *
     * The API returns the whole order, so I couldn't see a better way to
     * get the (newly-created) id of the most recently added item.
     */
    cartItemId: String(data.data.order.items.pop()!.id),
  };
}

/**
 * Mutation for adding the provided product to the user's cart
 * Proxies to eagle via nginx
 */
export function useAddToCart(
  options: UseMutationOptions<UseAddToCartResponse, AxiosError, AddToCartArgs>
) {
  return useMutation([], addToCart, options);
}

/**
 * Update the provided product in the user's cart.
 * Proxies to eagle via nginx
 */
async function updateCartItem({
  cartItemId,
  csrfToken,
  customisation,
  productSku,
}: UpdateCartItemArgs): Promise<UseUpdateCartItemResponse> {
  const {
    country_code,
    currency,
    guest_token,
    guestToken,
    locale,
    restricted_user_id,
    restrictedUserId,
  } = Cookies.get();

  const missingCookies = getMissingKeysFromObject({
    country_code,
    currency,
    locale,
  });

  if (missingCookies) {
    throw missingCookiesError(missingCookies);
  }

  const { data } = await axios.patch<UpdateCartItemResponse>(
    "/website/v2/cart/items/" + cartItemId,
    {
      item: {
        productId: productSku,
        customisations: customisation,
      },
    },
    {
      headers: {
        Lmn_locale: locale,
        Lmn_currency: currency,
        Lmn_country_iso: country_code,
        Lmn_guest_token: guestToken ?? guest_token,
        Lmn_restricted_user: restrictedUserId ?? restricted_user_id,
        "X-Csrf-Token": csrfToken,
      },
    }
  );

  return {
    customisation,
    newGuestToken: data.data.newGuestToken,
  };
}

/**
 * Mutation for updating the provided product in the user's cart
 * Proxies to eagle via nginx
 */
export function useUpdateCartItem(
  options: UseMutationOptions<
    UseUpdateCartItemResponse,
    AxiosError,
    UpdateCartItemArgs
  >
) {
  return useMutation([], updateCartItem, options);
}
